/**
 * King Living override: default `full` variant so nested image assets load when refetching.
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseKingDifferenceMediaCf013ById,
  getBaseKingDifferenceMediaCf013s,
  getFullKingDifferenceMediaCf013ById,
  getFullKingDifferenceMediaCf013s,
  getKingDifferenceMediaCf013ById,
  getKingDifferenceMediaCf013s,
} from '@amblique/contentful/app/contentful/queries';

export function useKingDifferenceMediaCf013ById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query =
    variant === 'base' ?
      getBaseKingDifferenceMediaCf013ById :
      variant === 'full' ?
        getFullKingDifferenceMediaCf013ById :
        getKingDifferenceMediaCf013ById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'kingDifferenceMediaCf013',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          id,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.kingDifferenceMediaCf013 || null;
    },

    enabled: !!contentfulService && !!id,

    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,

    ...queryOptions,
  });
}

export function useKingDifferenceMediaCf013s(options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query =
    variant === 'base' ?
      getBaseKingDifferenceMediaCf013s :
      variant === 'full' ?
        getFullKingDifferenceMediaCf013s :
        getKingDifferenceMediaCf013s;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'kingDifferenceMediaCf013',
      'collection',
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          limit: 10,
          skip: 0,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.kingDifferenceMediaCf013Collection || {total: 0, skip: 0, limit: 0, items: []};
    },

    enabled: !!contentfulService,

    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,

    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }

      return failureCount < 3;
    },

    ...queryOptions,
  });
}
